import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Table } from 'antd';
import Papa from 'papaparse';
import { projectsServices } from 'services';
import brandRoles from 'utils/brandRoles';
import ConfirmModal from 'scenes/Profile/components/ConfirmModal';
import {
  CountPill,
  FONT,
  LineIcon,
  Pagination,
  Section,
  SectionHeader,
  SectionTitle,
  Tag,
  TextButton,
  tableCss,
} from './ui';

const PER_PAGE = 10;

const formatPrice = (value) =>
  `${Number(value || 0).toLocaleString('de-DE', {
    maximumFractionDigits: 2,
  })} €`;

// Backend sends "Lastname,Firstname"
const formatName = (raw) => {
  const [last, first] = String(raw || '').split(',');
  return [first, last]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');
};

// Backend sends "street,number,zip,city,country"
const formatAddress = (raw) => {
  const [street, number, zip, city, country] = String(raw || '')
    .split(',')
    .map((part) => (part || '').trim());
  return [
    [street, number].filter(Boolean).join(' '),
    [zip, city].filter(Boolean).join(' '),
    country,
  ]
    .filter(Boolean)
    .join(', ');
};

// Goodies with shipping are "sent", all others (digital, pick-up, ...) are "done"
const wording = (reward) =>
  reward && reward.address_required
    ? {
        done: 'Versendet',
        mark: 'Als versendet markieren',
        count: 'versendet',
        hint: 'sobald es unterwegs ist',
      }
    : {
        done: 'Erledigt',
        mark: 'Als erledigt markieren',
        count: 'erledigt',
        hint: 'sobald du es übergeben oder verschickt hast',
      };

export class RewardsTable extends Component {
  static propTypes = {
    projectId: PropTypes.string.isRequired,
  };

  state = {
    rewards: [],
    selected: null,
    orders: [],
    labels: { variants: [], questions: [], extra: null },
    loadingOrders: false,
    page: 1,
    confirmOrder: null,
  };

  async componentDidMount() {
    try {
      const rewards = await projectsServices.getProjectRewards(
        this.props.projectId,
        true
      );
      this.setState({ rewards: Array.isArray(rewards) ? rewards : [] });
    } catch (e) {
      this.setState({ rewards: [] });
    }
  }

  openReward = async (reward) => {
    this.setState({ selected: reward, loadingOrders: true, page: 1, orders: [] });
    const [variants, questions] = await Promise.all([
      projectsServices.getRewardVariants(reward.id),
      projectsServices.getRewardQuestions(reward.id),
    ]);
    const labels = {
      variants: (variants[reward.id] || []).map((v) => v.attribute),
      questions: (questions[reward.id] || []).map((q) => q[0]),
      extra: variants.extra_details || null,
    };
    await this.loadOrders(reward.id);
    this.setState({ labels });
  };

  // The orders endpoint returns all orders at once, so we page on the client.
  loadOrders = async (rewardId) => {
    this.setState({ loadingOrders: true });
    try {
      const res = await projectsServices.getRewardOrder(rewardId, 1000, 1);
      this.setState({ orders: (res && res.orders) || [] });
    } finally {
      this.setState({ loadingOrders: false });
    }
  };

  toggleSent = async () => {
    const { confirmOrder, selected } = this.state;
    this.setState({ confirmOrder: null });
    await projectsServices.toggleOrderSent(confirmOrder.id);
    await this.loadOrders(selected.id);
  };

  downloadCSV = () => {
    const { orders, labels, selected } = this.state;
    const rows = orders.map((order) => {
      const row = {
        Name: formatName(order.name),
        'E-Mail': order.email,
      };
      if (selected.address_required) row.Anschrift = formatAddress(order.address);
      row.Menge = order.quantity;
      labels.variants.forEach((label) => {
        row[label] = order[label];
      });
      labels.questions.forEach((label) => {
        row[label] = order[label];
      });
      if (labels.extra) row[labels.extra] = order.extra_details;
      row[wording(selected).done] = order.sent ? 'ja' : 'nein';
      return row;
    });
    const blob = new Blob([Papa.unparse(rows)], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `goodies_${selected.id}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  columns() {
    const { selected, labels } = this.state;
    const columns = [
      {
        title: 'Unterstützer',
        key: 'name',
        width: 220,
        render: (_, order) => (
          <>
            <div className="cell-main ellipsis">{formatName(order.name)}</div>
            {order.email && (
              <div className="cell-sub ellipsis" title={order.email}>
                {order.email}
              </div>
            )}
          </>
        ),
      },
    ];
    if (selected.address_required) {
      columns.push({
        title: 'Anschrift',
        key: 'address',
        width: 220,
        render: (_, order) => (
          <div className="cell-text">{formatAddress(order.address)}</div>
        ),
      });
    }
    // All order details stacked in one column, so the table does not grow sideways
    // when a goodie has many variants and questions.
    const answerLabels = [...labels.variants, ...labels.questions];
    columns.push({
      title: 'Bestellung',
      key: 'order',
      render: (_, order) => {
        const details = answerLabels
          .map((label) => [label, order[label]])
          .concat(labels.extra ? [[labels.extra, order.extra_details]] : [])
          .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
        return (
          <div className="order-details">
            <div className="detail-line">
              <span className="detail-label">Anzahl:</span> {order.quantity}
            </div>
            {details.map(([label, value]) => (
              <div key={label} className="detail-line">
                <span className="detail-label">{label}:</span> {value}
              </div>
            ))}
          </div>
        );
      },
    });
    const words = wording(selected);
    columns.push({
      title: 'Status',
      key: 'sent',
      width: 200,
      render: (_, order) =>
        order.sent ? (
          <div className="sent">
            <Tag $tone="gruen">{words.done}</Tag>
            <TextButton
              type="button"
              className="quiet"
              onClick={() => this.setState({ confirmOrder: order })}
            >
              Zurücksetzen
            </TextButton>
          </div>
        ) : (
          <TextButton
            type="button"
            onClick={() => this.setState({ confirmOrder: order })}
          >
            {words.mark}
          </TextButton>
        ),
    });
    return columns;
  }

  renderList() {
    const { rewards } = this.state;
    return (
      <>
        <p className="intro">
          Für die Goodies bist du verantwortlich: Mit der Unterstützung
          schließt der Unterstützer einen Vertrag mit dir und hat Anspruch auf
          sein Goodie. Verzögert sich etwas, sag ihm früh Bescheid und biete
          eine Lösung an.
        </p>
        <ul className="reward-list">
          {rewards.map((reward) => (
            <li key={reward.id}>
              <button type="button" onClick={() => this.openReward(reward)}>
                {reward.image ? (
                  <img className="thumb" src={reward.image} alt="" />
                ) : (
                  <span className="thumb thumb--empty" aria-hidden>
                    <LineIcon name="geschenk" size={24} />
                  </span>
                )}
                <span className="reward-text">
                  <span className="reward-title">{reward.title}</span>
                  <span className="reward-meta">
                    {reward.ordered_reward_count || 0}
                    {reward.max_order_count
                      ? ` von ${reward.max_order_count}`
                      : ''}{' '}
                    gebucht · {formatPrice(reward.price)}
                  </span>
                </span>
                <span className="chevron" aria-hidden>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 12l4-4-4-4" />
                  </svg>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </>
    );
  }

  renderOrders() {
    const { selected, orders, loadingOrders, page } = this.state;
    const sentCount = orders.filter((o) => o.sent).length;
    const visible = orders.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    return (
      <>
        <div className="detail-head">
          <TextButton
            type="button"
            className="back"
            onClick={() => this.setState({ selected: null, orders: [] })}
          >
            <LineIcon name="zurueck" size={16} />
            Alle Goodies
          </TextButton>
          <div className="detail-title-row">
            <h3 className="detail-title">{selected.title}</h3>
            {orders.length > 0 && (
              <span className="detail-meta">
                {sentCount} von {orders.length} {wording(selected).count}
              </span>
            )}
            {orders.length > 0 && (
              <TextButton
                type="button"
                className="download"
                onClick={this.downloadCSV}
              >
                <LineIcon name="download" size={16} />
                CSV herunterladen
              </TextButton>
            )}
          </div>
        </div>
        {!loadingOrders && orders.length === 0 ? (
          <p className="intro">Für dieses Goodie gibt es noch keine Bestellungen.</p>
        ) : (
          <>
            <Table
              columns={this.columns()}
              dataSource={visible}
              rowKey="id"
              loading={loadingOrders}
              tableLayout="fixed"
              pagination={false}
              scroll={{ x: 720 }}
            />
            <Pagination
              page={page}
              pages={Math.ceil(orders.length / PER_PAGE)}
              onChange={(next) => this.setState({ page: next })}
            />
          </>
        )}
      </>
    );
  }

  confirmText(words) {
    const { confirmOrder } = this.state;
    if (!confirmOrder) return '';
    const name = formatName(confirmOrder.name) || 'diesen Unterstützer';
    if (confirmOrder.sent) {
      return `Das Goodie erscheint dann wieder als noch nicht ${words.count}.`;
    }
    return `Markiere das Goodie für ${name} als ${words.count}, ${words.hint}.`;
  }

  render() {
    const { rewards, selected, confirmOrder } = this.state;
    if (rewards.length === 0) return null;
    const words = wording(selected);

    return (
      <Wrapper>
        <Section>
          <SectionHeader>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SectionTitle>Goodies</SectionTitle>
              <CountPill>{rewards.length}</CountPill>
            </div>
          </SectionHeader>
          {selected ? this.renderOrders() : this.renderList()}
        </Section>

        <ConfirmModal
          open={!!confirmOrder}
          title={
            confirmOrder && confirmOrder.sent
              ? `${words.done === 'Versendet' ? 'Versand' : 'Status'} zurücksetzen?`
              : `${words.mark}?`
          }
          text={this.confirmText(words)}
          confirmLabel={
            confirmOrder && confirmOrder.sent ? 'Zurücksetzen' : words.mark
          }
          onCancel={() => this.setState({ confirmOrder: null })}
          onConfirm={this.toggleSent}
        />
      </Wrapper>
    );
  }
}

export default RewardsTable;

const Wrapper = styled.div`
  ${tableCss}

  .intro {
    margin: 0 0 16px;
    font-family: ${FONT};
    font-size: 14px;
    line-height: 21px;
    color: ${brandRoles.neutral700};
  }

  .reward-list {
    margin: 0;
    padding: 0;
    list-style: none;
    border: 1px solid ${brandRoles.neutral200};
    border-radius: 12px;
    overflow: hidden;

    li + li {
      border-top: 1px solid ${brandRoles.neutral200};
    }

    button {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border: 0;
      background: ${brandRoles.flaecheWeiss};
      text-align: left;
      font-family: ${FONT};
      cursor: pointer;

      &:hover {
        background: ${brandRoles.neutral50};
      }
    }
  }

  .thumb {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    border-radius: 8px;
    object-fit: cover;
  }

  .thumb--empty {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${brandRoles.neutral100};
    color: ${brandRoles.neutral400};
  }

  .order-details {
    display: flex;
    flex-direction: column;
    gap: 2px;
    color: ${brandRoles.neutral700};
    overflow-wrap: anywhere;
  }

  .detail-label {
    color: ${brandRoles.neutral500};
  }

  .reward-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .reward-title {
    font-size: 15px;
    font-weight: 600;
    line-height: 22px;
    color: ${brandRoles.neutral800};
  }

  .reward-meta {
    font-size: 13px;
    line-height: 20px;
    color: ${brandRoles.neutral500};
  }

  .chevron {
    color: ${brandRoles.neutral400};
    display: inline-flex;
  }

  .detail-head {
    margin-bottom: 16px;
    font-family: ${FONT};
  }

  .back,
  .download {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .back {
    text-decoration: none;
    color: ${brandRoles.neutral700};
  }

  .detail-title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 12px;
    margin-top: 12px;
  }

  .detail-title {
    margin: 0;
    font-family: ${FONT};
    font-size: 16px;
    font-weight: 600;
    color: ${brandRoles.neutral800};
  }

  .detail-meta {
    font-size: 13px;
    color: ${brandRoles.neutral500};
  }

  .download {
    margin-left: auto;
  }

  .cell-main {
    color: ${brandRoles.neutral800};
  }

  .cell-sub {
    font-size: 13px;
    color: ${brandRoles.neutral500};
  }

  .cell-text {
    color: ${brandRoles.neutral700};
    overflow-wrap: anywhere;
  }

  .ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sent {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .quiet {
    color: ${brandRoles.neutral500};
    font-size: 13px;
  }
`;
